import type {
  RealtimeMediaSessionMetadata,
  RealtimeMediaSignalPayload,
  RealtimeRecipientDescriptor,
} from '@automatick/shared-ng';
import {
  RealtimeMediaSignalKind,
  type RealtimeMediaSignalKind as RealtimeMediaSignalKindValue,
} from '@automatick/shared-ts';
import {
  createRealtimeMediaSessionSnapshot,
  type RealtimeMediaSessionView,
} from './realtime-media-session.snapshot';

export type {
  RealtimeMediaControlMode,
  RealtimeMediaSessionView,
} from './realtime-media-session.snapshot';

export interface RealtimeMediaPeerPlatform {
  createPeerConnection(configuration: RTCConfiguration): RTCPeerConnection;
  createMediaStream(): MediaStream;
  createSessionDescription(
    description: RTCSessionDescriptionInit,
  ): RTCSessionDescription;
  createIceCandidate(candidate: RTCIceCandidateInit): RTCIceCandidate;
  getDisplayMedia(constraints: DisplayMediaStreamOptions): Promise<MediaStream>;
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
}

export interface RealtimeMediaCaptureLease {
  readonly captureId: number;
}

export interface RealtimeMediaProviderAttachment {
  readonly sourceKinds: readonly string[];
  readonly exchangeSdp: (offerSdp: string) => Promise<string>;
}

export type RealtimeMediaPeerRuntimeEvent =
  | { readonly kind: 'state-changed' }
  | {
      readonly kind: 'ice-candidate';
      readonly mediaSessionId: string;
      readonly candidate: RTCIceCandidate;
      readonly targetRecipients: readonly RealtimeRecipientDescriptor[];
      readonly metadata: RealtimeMediaSessionMetadata | null;
    }
  | {
      readonly kind: 'signal-request';
      readonly mediaSessionId: string;
      readonly signalKind: RealtimeMediaSignalKindValue;
      readonly targetRecipients: readonly RealtimeRecipientDescriptor[];
      readonly payload: RealtimeMediaSignalPayload | null;
      readonly metadata: RealtimeMediaSessionMetadata | null;
    };

interface LocalMediaCapture {
  readonly stream: MediaStream;
  readonly captureStreams: readonly MediaStream[];
}

interface ManagedPeerSession {
  view: RealtimeMediaSessionView;
  connection: RTCPeerConnection | null;
  pendingOffer: RTCSessionDescriptionInit | null;
  captureStreams: readonly MediaStream[];
  operationGeneration: number;
}

interface PeerOperationOwner {
  readonly session: ManagedPeerSession;
  readonly generation: number;
  readonly connection: RTCPeerConnection;
}

type SessionPatch = Omit<Partial<RealtimeMediaSessionView>, 'mediaSessionId'>;
type PeerMode = 'peer' | 'provider';
type RuntimeEventPublisher = (
  event: RealtimeMediaPeerRuntimeEvent,
) => void | Promise<void>;

const startFailure = 'Realtime media session could not be started.';
const connectFailure = 'Realtime media session could not be connected.';
const updateFailure = 'Live media connection could not be updated.';
const signalingFailure = 'Realtime media signaling failed.';

export class RealtimeMediaPeerRuntime {
  private readonly sessions = new Map<string, ManagedPeerSession>();
  private readonly pendingCaptures = new Map<
    RealtimeMediaCaptureLease,
    LocalMediaCapture
  >();
  private readonly releasedConnections = new WeakSet<RTCPeerConnection>();
  private readonly stoppedTracks = new WeakSet<MediaStreamTrack>();
  private nextCaptureId = 0;
  private destroyed = false;

  constructor(
    private readonly platform: RealtimeMediaPeerPlatform,
    private readonly configuration: RTCConfiguration,
    private readonly publish: RuntimeEventPublisher,
  ) {}

  get snapshot(): readonly RealtimeMediaSessionView[] {
    return Object.freeze(
      [...this.sessions.values()].map((session) => session.view),
    );
  }

  getSession(mediaSessionId: string): RealtimeMediaSessionView | null {
    return this.resolveSession(mediaSessionId)?.view ?? null;
  }

  async capture(
    sourceKinds: readonly string[],
  ): Promise<RealtimeMediaCaptureLease | null> {
    if (this.destroyed) return null;
    let capture: LocalMediaCapture;
    try {
      capture = await this.acquireCapture(sourceKinds);
    } catch {
      return null;
    }
    if (this.destroyed) {
      this.disposeCapture(capture);
      return null;
    }
    const lease = Object.freeze({ captureId: ++this.nextCaptureId });
    this.pendingCaptures.set(lease, capture);
    return lease;
  }

  releaseCapture(lease: RealtimeMediaCaptureLease): void {
    const capture = this.takeCapture(lease);
    if (capture) this.disposeCapture(capture);
  }

  async startOutbound(
    view: RealtimeMediaSessionView,
    captureLease: RealtimeMediaCaptureLease,
    inviteMetadata: RealtimeMediaSessionMetadata,
  ): Promise<boolean> {
    const capture = this.takeCapture(captureLease);
    if (!capture) return false;
    const session = this.adoptSession(
      { ...view, localStream: capture.stream },
      null,
      capture.captureStreams,
    );
    if (!session) {
      this.disposeCapture(capture);
      return false;
    }

    let owner: PeerOperationOwner | null = null;
    try {
      const connection = this.attachConnection(session, 'peer');
      owner = this.beginOperation(session, connection);
      this.addLocalTracks(owner);
      const offer = await this.createLocalDescription(owner, null, true);
      if (!offer) return this.failCurrent(session, owner, startFailure);
      if (!this.ownsOperation(owner)) return false;
      session.pendingOffer = offer;
      this.patchOwned(owner, { phase: 'connecting' });
      const payload = serializeSessionDescription(offer);
      if (!payload) return this.failCurrent(session, owner, startFailure);
      return this.publishOwned(
        owner,
        {
          kind: 'signal-request',
          mediaSessionId: session.view.mediaSessionId,
          signalKind: RealtimeMediaSignalKind.Invite,
          targetRecipients: session.view.targetRecipients,
          payload,
          metadata: inviteMetadata,
        },
        signalingFailure,
      );
    } catch {
      return this.failCurrent(session, owner, startFailure);
    }
  }

  addInbound(
    view: RealtimeMediaSessionView,
    payload: RealtimeMediaSignalPayload | null,
  ): boolean {
    const offer = readSessionDescription(payload, 'offer');
    const session = offer ? this.adoptSession(view, offer) : null;
    if (!session) return false;
    this.emitState();
    return true;
  }

  async accept(
    mediaSessionId: string,
    sourceKinds: readonly string[],
    requestedMediaKinds: readonly string[],
  ): Promise<boolean> {
    const session = this.resolveSession(mediaSessionId);
    if (!session?.pendingOffer) return false;
    const pendingOffer = session.pendingOffer;
    const initialConnection = session.connection;
    const generation = ++session.operationGeneration;
    let capture: LocalMediaCapture | null = null;
    let owner: PeerOperationOwner | null = null;
    try {
      capture = await this.acquireCapture(sourceKinds);
      if (!this.owns(session, initialConnection, generation)) {
        this.disposeCapture(capture);
        return false;
      }

      this.stopStreams([session.view.localStream, ...session.captureStreams]);
      session.captureStreams = capture.captureStreams;
      session.view = createRealtimeMediaSessionSnapshot({
        ...session.view,
        localStream: capture.stream,
        preferredSourceKinds: [...sourceKinds],
        requestedMediaKinds: [...requestedMediaKinds],
        phase: 'connecting',
        errorMessage: null,
      });
      const connection = this.attachConnection(session, 'peer');
      owner = { session, generation, connection };
      this.addLocalTracks(owner);
      const payload = serializeSessionDescription(
        await this.createLocalDescription(owner, pendingOffer, false),
      );
      if (!payload) return this.failCurrent(session, owner, connectFailure);
      if (!this.ownsOperation(owner)) return false;
      session.pendingOffer = null;
      const published = await this.publishOwned(
        owner,
        {
          kind: 'signal-request',
          mediaSessionId,
          signalKind: RealtimeMediaSignalKind.Answer,
          targetRecipients: session.view.replyRecipients,
          payload,
          metadata: {
            ...(session.view.metadata ?? {}),
            requestedMediaKinds: [...session.view.requestedMediaKinds],
            preferredSourceKinds: [...session.view.preferredSourceKinds],
          },
        },
        signalingFailure,
      );
      if (published && this.ownsOperation(owner)) this.emitState();
      return published;
    } catch {
      const ownsFailure = owner
        ? this.ownsOperation(owner)
        : this.owns(session, initialConnection, generation);
      if (ownsFailure) {
        this.settleSession(session, connectFailure);
      } else if (capture) {
        this.disposeCapture(capture);
      }
      return false;
    }
  }

  async applyAnswer(
    mediaSessionId: string,
    payload: RealtimeMediaSignalPayload | null,
  ): Promise<void> {
    const session = this.resolveSession(mediaSessionId);
    const description = readSessionDescription(payload, 'answer');
    if (!session) return;
    if (!description || !session.connection) {
      this.settleSession(session, 'Remote connection could not be completed.');
      return;
    }
    const owner = this.beginOperation(session, session.connection);
    try {
      await owner.connection.setRemoteDescription(
        this.platform.createSessionDescription(description),
      );
      if (this.ownsOperation(owner)) {
        this.patchOwned(owner, { phase: 'connected' });
      }
    } catch {
      this.failCurrent(
        session,
        owner,
        'Remote connection could not be completed.',
      );
    }
  }

  async answerOffer(
    mediaSessionId: string,
    payload: RealtimeMediaSignalPayload | null,
    replyRecipients: readonly RealtimeRecipientDescriptor[],
  ): Promise<void> {
    const session = this.resolveSession(mediaSessionId);
    const description = readSessionDescription(payload, 'offer');
    if (!session) return;
    if (!description || !session.connection || replyRecipients.length === 0) {
      this.settleSession(session, updateFailure);
      return;
    }
    const owner = this.beginOperation(session, session.connection);
    try {
      const answerPayload = serializeSessionDescription(
        await this.createLocalDescription(owner, description, false),
      );
      if (!answerPayload) {
        this.failCurrent(session, owner, updateFailure);
        return;
      }
      if (!this.ownsOperation(owner)) return;
      await this.publishOwned(
        owner,
        {
          kind: 'signal-request',
          mediaSessionId,
          signalKind: RealtimeMediaSignalKind.Answer,
          targetRecipients: replyRecipients.map((recipient) => ({
            ...recipient,
          })),
          payload: answerPayload,
          metadata: session.view.metadata,
        },
        signalingFailure,
      );
    } catch {
      this.failCurrent(session, owner, updateFailure);
    }
  }

  async addIceCandidate(
    mediaSessionId: string,
    candidate: RTCIceCandidateInit | null,
  ): Promise<void> {
    const session = this.resolveSession(mediaSessionId);
    const connection = session?.connection;
    if (!session || !connection || !candidate) return;
    const owner: PeerOperationOwner = {
      session,
      connection,
      generation: session.operationGeneration,
    };
    try {
      await connection.addIceCandidate(
        this.platform.createIceCandidate(candidate),
      );
    } catch {
      if (this.ownsOperation(owner)) {
        this.settleSession(session, updateFailure);
      }
    }
  }

  async startProviderSession(
    view: RealtimeMediaSessionView,
    attachment: RealtimeMediaProviderAttachment | null,
  ): Promise<boolean> {
    const session = this.adoptSession(view, null);
    if (!session) return false;
    const attached = attachment
      ? await this.attachProvider(session, attachment)
      : true;
    if (attached && this.ownsSession(session)) this.emitState();
    return this.ownsSession(session);
  }

  patch(mediaSessionId: string, patch: SessionPatch): boolean {
    const session = this.resolveSession(mediaSessionId);
    if (!session) return false;
    session.view = createRealtimeMediaSessionSnapshot({
      ...session.view,
      ...patch,
    });
    this.emitState();
    return true;
  }

  remove(mediaSessionId: string): boolean {
    const session = this.resolveSession(mediaSessionId);
    if (!session) return false;
    this.releaseSessionResources(session);
    this.sessions.delete(mediaSessionId);
    this.emitState();
    return true;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const session of this.sessions.values())
      this.releaseSessionResources(session);
    this.sessions.clear();
    for (const capture of this.pendingCaptures.values())
      this.disposeCapture(capture);
    this.pendingCaptures.clear();
  }

  private async attachProvider(
    session: ManagedPeerSession,
    attachment: RealtimeMediaProviderAttachment,
  ): Promise<boolean> {
    const initialConnection = session.connection;
    const generation = ++session.operationGeneration;
    let capture: LocalMediaCapture | null = null;
    let connection: RTCPeerConnection | null = null;
    try {
      capture = await this.acquireCapture(attachment.sourceKinds);
      if (!this.owns(session, initialConnection, generation)) {
        this.disposeCapture(capture);
        return false;
      }
      session.captureStreams = capture.captureStreams;
      session.view = createRealtimeMediaSessionSnapshot({
        ...session.view,
        localStream: capture.stream,
        preferredSourceKinds: [...attachment.sourceKinds],
        phase: 'connecting',
        errorMessage: null,
      });
      connection = this.attachConnection(session, 'provider');
      const owner: PeerOperationOwner = { session, generation, connection };
      this.addLocalTracks(owner);
      const offerSdp = (await this.createLocalDescription(owner, null, false))
        ?.sdp;
      if (!offerSdp)
        throw new Error('Provider WebRTC offer did not produce SDP.');
      const answerSdp = await attachment.exchangeSdp(offerSdp);
      if (!this.ownsOperation(owner)) return false;
      if (answerSdp.length === 0)
        throw new Error('Provider WebRTC answer did not produce SDP.');
      await connection.setRemoteDescription(
        this.platform.createSessionDescription({
          type: 'answer',
          sdp: answerSdp,
        }),
      );
      return this.ownsOperation(owner);
    } catch {
      const ownsFailure = this.owns(
        session,
        connection ?? initialConnection,
        generation,
      );
      if (!ownsFailure) {
        if (connection) this.releasePeerConnection(connection);
        if (capture) this.disposeCapture(capture);
        return false;
      }
      this.settleSession(session, 'Provider media attachment failed.');
      return false;
    }
  }

  private async createLocalDescription(
    owner: PeerOperationOwner,
    remoteOffer: RTCSessionDescriptionInit | null,
    receivesVideo: boolean,
  ): Promise<RTCSessionDescriptionInit | null> {
    if (remoteOffer) {
      await owner.connection.setRemoteDescription(
        this.platform.createSessionDescription(remoteOffer),
      );
      if (!this.ownsOperation(owner)) return null;
    }
    const description = remoteOffer
      ? await owner.connection.createAnswer()
      : await owner.connection.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: receivesVideo,
        });
    if (!this.ownsOperation(owner)) return null;
    await owner.connection.setLocalDescription(description);
    return this.ownsOperation(owner)
      ? (owner.connection.localDescription ?? description)
      : null;
  }

  private attachConnection(
    session: ManagedPeerSession,
    mode: PeerMode,
  ): RTCPeerConnection {
    if (session.connection) return session.connection;
    const connection = this.platform.createPeerConnection(this.configuration);
    const remoteStream = this.platform.createMediaStream();
    if (mode === 'peer') {
      connection.onicecandidate = (event) => {
        if (!event.candidate || !this.owns(session, connection)) return;
        const owner: PeerOperationOwner = {
          session,
          connection,
          generation: session.operationGeneration,
        };
        void this.publishOwned(
          owner,
          {
            kind: 'ice-candidate',
            mediaSessionId: session.view.mediaSessionId,
            candidate: event.candidate,
            targetRecipients:
              session.view.direction === 'outbound'
                ? session.view.targetRecipients
                : session.view.replyRecipients,
            metadata: session.view.metadata,
          },
          signalingFailure,
        );
      };
    }
    connection.ontrack = (event) => {
      if (!this.owns(session, connection)) return;
      for (const track of event.streams[0]?.getTracks() ?? []) {
        remoteStream.addTrack(track);
      }
      if (!this.owns(session, connection)) return;
      session.view = createRealtimeMediaSessionSnapshot({
        ...session.view,
        remoteStream,
        phase: 'connected',
        errorMessage: null,
      });
      this.emitState();
    };
    connection.onconnectionstatechange = () => {
      if (!this.owns(session, connection)) return;
      const state = connection.connectionState;
      if (state === 'connected') {
        session.view = createRealtimeMediaSessionSnapshot({
          ...session.view,
          phase: 'connected',
          errorMessage: null,
        });
        this.emitState();
        return;
      }
      if (state === 'failed') {
        this.settleSession(
          session,
          mode === 'peer'
            ? 'Peer connection failed.'
            : 'Provider media connection failed.',
        );
        return;
      }
      if (state === 'disconnected' || state === 'closed')
        this.settleSession(session, null);
    };
    session.connection = connection;
    return connection;
  }

  private async acquireCapture(
    sourceKinds: readonly string[],
  ): Promise<LocalMediaCapture> {
    const wantsScreen = sourceKinds.includes('screen');
    const wantsSpeaker = sourceKinds.includes('speaker');
    const wantsCamera =
      sourceKinds.includes('camera') || sourceKinds.includes('video');
    const wantsMicrophone =
      sourceKinds.includes('microphone') || sourceKinds.includes('audio');
    const streams: MediaStream[] = [];
    try {
      if (wantsScreen || wantsSpeaker) {
        const constraints = { video: true, audio: wantsSpeaker };
        streams.push(await this.platform.getDisplayMedia(constraints));
      }
      if (wantsCamera || wantsMicrophone) {
        const constraints = { video: wantsCamera, audio: wantsMicrophone };
        streams.push(await this.platform.getUserMedia(constraints));
      }
    } catch (error: unknown) {
      this.stopStreams(streams);
      throw error;
    }
    const merged = this.platform.createMediaStream();
    for (const stream of streams) {
      const keepsVideo = wantsScreen || stream.getAudioTracks().length === 0;
      for (const track of stream.getTracks()) {
        if (track.kind !== 'video' || keepsVideo) merged.addTrack(track);
      }
    }
    return { stream: merged, captureStreams: Object.freeze([...streams]) };
  }

  private adoptSession(
    view: RealtimeMediaSessionView,
    pendingOffer: RTCSessionDescriptionInit | null,
    captureStreams: readonly MediaStream[] = [],
  ): ManagedPeerSession | null {
    const mediaSessionId = exactIdentity(view.mediaSessionId);
    if (this.destroyed || !mediaSessionId || this.sessions.has(mediaSessionId))
      return null;
    const session: ManagedPeerSession = {
      view: createRealtimeMediaSessionSnapshot(view),
      connection: null,
      pendingOffer: pendingOffer ? { ...pendingOffer } : null,
      captureStreams: Object.freeze([...captureStreams]),
      operationGeneration: 0,
    };
    this.sessions.set(mediaSessionId, session);
    return session;
  }

  private resolveSession(mediaSessionId: string): ManagedPeerSession | null {
    const identity = exactIdentity(mediaSessionId);
    return !this.destroyed && identity
      ? (this.sessions.get(identity) ?? null)
      : null;
  }

  private beginOperation(
    session: ManagedPeerSession,
    connection: RTCPeerConnection,
  ): PeerOperationOwner {
    return { session, connection, generation: ++session.operationGeneration };
  }

  private ownsOperation(owner: PeerOperationOwner): boolean {
    return this.owns(owner.session, owner.connection, owner.generation);
  }

  private owns(
    session: ManagedPeerSession,
    connection: RTCPeerConnection | null,
    generation?: number,
  ): boolean {
    return (
      this.ownsSession(session) &&
      session.connection === connection &&
      (generation === undefined || session.operationGeneration === generation)
    );
  }

  private ownsSession(session: ManagedPeerSession): boolean {
    return (
      !this.destroyed &&
      this.sessions.get(session.view.mediaSessionId) === session
    );
  }

  private addLocalTracks(owner: PeerOperationOwner): void {
    const stream = owner.session.view.localStream;
    if (!stream || !this.ownsOperation(owner)) return;
    for (const track of stream.getTracks())
      owner.connection.addTrack(track, stream);
  }

  private patchOwned(owner: PeerOperationOwner, patch: SessionPatch): void {
    if (!this.ownsOperation(owner)) return;
    owner.session.view = createRealtimeMediaSessionSnapshot({
      ...owner.session.view,
      ...patch,
    });
    this.emitState();
  }

  private takeCapture(
    lease: RealtimeMediaCaptureLease,
  ): LocalMediaCapture | null {
    const capture = this.pendingCaptures.get(lease) ?? null;
    this.pendingCaptures.delete(lease);
    return capture;
  }

  private releasePeerConnection(connection: RTCPeerConnection): void {
    if (this.releasedConnections.has(connection)) return;
    this.releasedConnections.add(connection);
    connection.onicecandidate = null;
    connection.ontrack = null;
    connection.onconnectionstatechange = null;
    connection.close();
  }

  private disposeCapture(capture: LocalMediaCapture): void {
    this.stopStreams([capture.stream, ...capture.captureStreams]);
  }

  private stopStreams(streams: readonly (MediaStream | null)[]): void {
    for (const stream of streams) {
      for (const track of stream?.getTracks() ?? []) {
        if (this.stoppedTracks.has(track)) continue;
        this.stoppedTracks.add(track);
        track.stop();
      }
    }
  }

  private settleSession(
    session: ManagedPeerSession,
    errorMessage: string | null,
  ): void {
    if (!this.ownsSession(session)) return;
    this.releaseSessionResources(session);
    session.view = createRealtimeMediaSessionSnapshot({
      ...session.view,
      localStream: null,
      remoteStream: null,
      phase: errorMessage ? 'error' : 'ended',
      errorMessage,
    });
    this.emitState();
  }

  private releaseSessionResources(session: ManagedPeerSession): void {
    session.operationGeneration += 1;
    if (session.connection) this.releasePeerConnection(session.connection);
    this.stopStreams([
      session.view.localStream,
      session.view.remoteStream,
      ...session.captureStreams,
    ]);
    session.connection = null;
    session.pendingOffer = null;
    session.captureStreams = Object.freeze([]);
  }

  private emitState(): void {
    if (this.destroyed) return;
    try {
      void Promise.resolve(this.publish({ kind: 'state-changed' })).catch(
        () => undefined,
      );
    } catch {
      return;
    }
  }

  private async publishOwned(
    owner: PeerOperationOwner,
    event: RealtimeMediaPeerRuntimeEvent,
    failureMessage: string,
  ): Promise<boolean> {
    if (!this.ownsOperation(owner)) return false;
    try {
      await this.publish(event);
      return this.ownsOperation(owner);
    } catch {
      this.failCurrent(owner.session, owner, failureMessage);
      return false;
    }
  }

  private failCurrent(
    session: ManagedPeerSession,
    owner: PeerOperationOwner | null,
    errorMessage: string,
  ): false {
    if (owner ? this.ownsOperation(owner) : this.ownsSession(session)) {
      this.settleSession(session, errorMessage);
    }
    return false;
  }
}

function readSessionDescription(
  payload: RealtimeMediaSignalPayload | null,
  expectedType: 'offer' | 'answer',
): RTCSessionDescriptionInit | null {
  const sdp = payload?.sdp;
  return payload?.type === expectedType &&
    typeof sdp === 'string' &&
    sdp.trim().length > 0
    ? { type: expectedType, sdp }
    : null;
}

function serializeSessionDescription(
  description: RTCSessionDescription | RTCSessionDescriptionInit | null,
): RealtimeMediaSignalPayload | null {
  return description?.type && description.sdp
    ? { type: description.type, sdp: description.sdp }
    : null;
}

function exactIdentity(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.length > 0 && value === value.trim()
    ? value
    : null;
}
